import { Injectable, signal, computed } from '@angular/core';
import { Routine } from 'app/modules/admin/models/routines.types';

/**
 * Servicio para gestionar el estado de las rutinas seleccionadas
 * Maneja el cache en localStorage y asegura que siempre haya una rutina vacía
 */
@Injectable()
export class RoutineDetonatorStateService {
    private readonly CACHE_KEY = 'routineDetonator.selectedRoutines';

    // Estado reactivo con signals
    private _selectedRoutines = signal<Routine[]>([]);

    // Señal computada para verificar si hay rutinas listas
    readonly selectedRoutines = this._selectedRoutines.asReadonly();

    readonly hasReadyRoutines = computed(() => {
        return this._selectedRoutines().some(r => {
            const allDevicesAssigned = r.cluster?.type_devices?.every(td => !!td.selected_device_id);
            return !!r.selected_user_id && allDevicesAssigned;
        });
    });

    constructor() {
        this.loadFromCache();
        this.ensureEmptyRoutineAtEnd();
    }

    /**
     * Obtiene las rutinas seleccionadas
     */
    getSelectedRoutines(): Routine[] {
        return this._selectedRoutines();
    }

    /**
     * Actualiza las rutinas seleccionadas
     */
    setSelectedRoutines(routines: Routine[]): void {
        this._selectedRoutines.set(routines);
        this.saveToCache();
    }

    /**
     * Agrega una rutina a la lista
     */
    addRoutine(routine: Routine): void {
        const cloned = structuredClone(routine);

        // Inicializa los selected_device_id
        if (cloned.cluster?.type_devices) {
            cloned.cluster.type_devices.forEach(td => {
                td.selected_device_id = null;
            });
        }

        const current = this._selectedRoutines();

        // Remover la rutina vacía si existe
        const filtered = current.filter(r => r.id !== null);

        // Agregar la nueva rutina y asegurar rutina vacía al final
        this._selectedRoutines.set([...filtered, cloned]);
        this.ensureEmptyRoutineAtEnd();
        this.saveToCache();
    }

    /**
     * Reemplaza una rutina en un índice específico
     */
    replaceRoutine(index: number, routine: Routine): void {
        const cloned = structuredClone(routine);

        // Inicializa los selected_device_id
        if (cloned.cluster?.type_devices) {
            cloned.cluster.type_devices.forEach(td => {
                td.selected_device_id = null;
            });
        }

        const current = [...this._selectedRoutines()];
        current[index] = {
            ...cloned,
            selected_user_id: null
        };

        this._selectedRoutines.set(current);
        this.ensureEmptyRoutineAtEnd();
        this.saveToCache();
    }

    /**
     * Elimina una rutina por índice
     */
    removeRoutine(index: number): void {
        const current = [...this._selectedRoutines()];
        current.splice(index, 1);
        this._selectedRoutines.set(current);
        this.ensureEmptyRoutineAtEnd();
        this.saveToCache();
    }

    /**
     * Actualiza una rutina específica (para cambios inmutables)
     */
    updateRoutine(index: number, updater: (routine: Routine) => Routine): void {
        const current = [...this._selectedRoutines()];
        const oldRoutine = current[index];
        const newRoutine = updater(structuredClone(oldRoutine));
        current[index] = newRoutine;
        this._selectedRoutines.set(current);
        this.saveToCache();
    }

    updateRoutineByExecutionId(executionId: string, updater: (routine: Routine) => Routine): void {
        const current = [...this._selectedRoutines()];
        const index = current.findIndex(r => r.execution_id === executionId);
        if (index === -1) return; // No encontrada
        const oldRoutine = current[index];
        const newRoutine = updater(structuredClone(oldRoutine));
        current[index] = newRoutine;
        this._selectedRoutines.set(current);
        this.saveToCache();
    }

    updateRoutineByRoutineLogId(routineLogId: number, updater: (routine: Routine) => Routine): void {
        const current = [...this._selectedRoutines()];
        const index = current.findIndex(r => r.routine_log_id === routineLogId);
        if (index === -1) return; // No encontrada
        const oldRoutine = current[index];
        const newRoutine = updater(structuredClone(oldRoutine));
        current[index] = newRoutine;
        this._selectedRoutines.set(current);
        this.saveToCache();
    }

    /**
     * Asigna un usuario a una rutina
     */
    assignUserToRoutine(routineIndex: number, userId: number): void {
        this.updateRoutine(routineIndex, routine => ({
            ...routine,
            selected_user_id: userId
        }));
    }

    /**
     * Asigna un dispositivo a una posición específica
     */
    assignDeviceToPosition(routineIndex: number, position: string, deviceId: number): void {
        this.updateRoutine(routineIndex, routine => {
            const typeDevice = routine.cluster?.type_devices?.find(td => td.pivot?.position === position);
            if (typeDevice) {
                typeDevice.selected_device_id = deviceId;
            }
            return routine;
        });
    }

    /**
     * Desasigna un dispositivo de una posición específica
     */
    unassignDeviceFromPosition(routineIndex: number, position: string): void {
        this.updateRoutine(routineIndex, routine => {
            const typeDevice = routine.cluster?.type_devices?.find(td => td.pivot?.position === position);
            if (typeDevice) {
                typeDevice.selected_device_id = null;
            }
            return routine;
        });
    }

    /**
     * Asegura que siempre haya una rutina vacía al final
     */
    private ensureEmptyRoutineAtEnd(): void {
        const current = this._selectedRoutines();

        // Si no hay rutinas, crear una vacía
        if (current.length === 0) {
            this._selectedRoutines.set([{ id: null, name: '' } as Routine]);
            return;
        }

        // Verificar si ya hay una rutina vacía
        const hasEmptyRoutine = current.some(r => r.id === null);

        if (!hasEmptyRoutine) {
            // No hay rutina vacía, agregarla
            this._selectedRoutines.set([...current, { id: null, name: '' } as Routine]);
        } else {
            // Hay rutina vacía, verificar que esté al final
            const emptyRoutineIndex = current.findIndex(r => r.id === null);
            if (emptyRoutineIndex !== current.length - 1) {
                const updated = [...current];
                const emptyRoutine = updated.splice(emptyRoutineIndex, 1)[0];
                updated.push(emptyRoutine);
                this._selectedRoutines.set(updated);
            }
        }
    }

    /**
     * Guarda el estado en localStorage
     */
    private saveToCache(): void {
        try {
            localStorage.setItem(this.CACHE_KEY, JSON.stringify(this._selectedRoutines()));
        } catch (e) {
            console.warn('No se pudo guardar el cache:', e);
        }
    }

    /**
     * Carga el estado desde localStorage
     */
    private loadFromCache(): void {
        const json = localStorage.getItem(this.CACHE_KEY);
        if (!json) return;

        try {
            const parsed = JSON.parse(json) as Routine[];
            this._selectedRoutines.set(parsed);
        } catch (e) {
            console.warn('Cache corrupto, se ignora:', e);
        }
    }

    /**
     * Limpia el cache
     */
    clearCache(): void {
        localStorage.removeItem(this.CACHE_KEY);
        this._selectedRoutines.set([]);
        this.ensureEmptyRoutineAtEnd();
    }
}
